import { memo, useState, useCallback } from 'react';
import {
  Button,
  Input,
  Label,
  Checkbox,
  Separator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@flote/ui';
import { Loader2, UsersRound, Building2, Settings, User, UserCog } from 'lucide-react';
import type { CreateTeamRequest, Hub, Driver } from '@flote/contracts/http';

export type TeamFormData = CreateTeamRequest;

interface TeamFormProps {
  onSubmit: (data: TeamFormData) => Promise<void>;
  isProcessing?: boolean;
  hubs: Hub[];
  drivers: Driver[];
  initialData?: Partial<TeamFormData>;
  mode?: 'create' | 'edit';
}

export const TeamForm = memo<TeamFormProps>(({
  onSubmit,
  isProcessing = false,
  hubs,
  drivers,
  initialData,
  mode = 'create',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const NO_HUB_VALUE = '__none__';

  // Form fields
  const [name, setName] = useState(initialData?.name || '');
  const [hub, setHub] = useState<string>(initialData?.hub || NO_HUB_VALUE);
  const [selectedWorkers, setSelectedWorkers] = useState<string[]>(initialData?.workers || []);
  const [selectedManagers, setSelectedManagers] = useState<string[]>(initialData?.managers || []);
  const [enableSelfAssignment, setEnableSelfAssignment] = useState(
    initialData?.enableSelfAssignment ?? false
  );

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Team name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildTeamRequest = (): TeamFormData => {
    return {
      name: name.trim(),
      hub: hub === NO_HUB_VALUE ? undefined : hub,
      enableSelfAssignment,
      workers: selectedWorkers,
      managers: selectedManagers,
    };
  };

  const handleWorkerToggle = (onfleetWorkerId: string) => {
    setSelectedWorkers(prev =>
      prev.includes(onfleetWorkerId)
        ? prev.filter(id => id !== onfleetWorkerId)
        : [...prev, onfleetWorkerId]
    );
  };

  const handleManagerToggle = (onfleetWorkerId: string) => {
    setSelectedManagers(prev =>
      prev.includes(onfleetWorkerId)
        ? prev.filter(id => id !== onfleetWorkerId)
        : [...prev, onfleetWorkerId]
    );
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const teamRequest = buildTeamRequest();
      await onSubmit(teamRequest);
    } finally {
      setIsSubmitting(false);
    }
  }, [name, hub, selectedWorkers, selectedManagers, enableSelfAssignment, onSubmit]);

  const processing = isSubmitting || isProcessing;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Team Name Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <UsersRound className="h-4 w-4 text-muted-foreground" />
          Team Information
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Team Name *</Label>
          <Input
            id="name"
            placeholder="e.g., North Zone Delivery"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={processing}
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && (
            <p className="text-xs text-destructive">{errors.name}</p>
          )}
        </div>
      </div>

      <Separator />

      {/* Hub Assignment Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          Hub Assignment
          <span className="text-xs font-normal text-muted-foreground">(optional)</span>
        </div>

        <div className="space-y-2">
          <Label htmlFor="hub">Assigned Hub</Label>
          <Select value={hub} onValueChange={setHub}>
            <SelectTrigger>
              <SelectValue placeholder="Select a hub (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_HUB_VALUE}>No Hub</SelectItem>
              {hubs.map((h) => (
                <SelectItem key={h.id} value={h.id}>
                  {h.name}
                  {h.address?.city && (
                    <span className="ml-2 text-muted-foreground">
                      ({h.address.city})
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Hubs are home bases for teams. Required for route optimization.
          </p>
        </div>
      </div>

      <Separator />

      {/* Drivers (Workers) Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <User className="h-4 w-4 text-muted-foreground" />
          Drivers
          <span className="text-xs font-normal text-muted-foreground">(optional)</span>
        </div>

        {drivers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No drivers available. Create a driver first.
          </p>
        ) : (
          <div className="space-y-2">
            <div className="grid gap-2">
              {drivers.map((driver) => (
                <div key={driver.onfleetWorkerId} className="flex items-center space-x-2">
                  <Checkbox
                    id={`worker-${driver.onfleetWorkerId}`}
                    checked={selectedWorkers.includes(driver.onfleetWorkerId)}
                    onCheckedChange={() => handleWorkerToggle(driver.onfleetWorkerId)}
                    disabled={processing}
                  />
                  <Label
                    htmlFor={`worker-${driver.onfleetWorkerId}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {driver.name}
                    {driver.displayName && (
                      <span className="ml-1 text-muted-foreground">
                        ({driver.displayName})
                      </span>
                    )}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Select drivers who are members of this team
            </p>
          </div>
        )}
      </div>

      <Separator />

      {/* Managers Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <UserCog className="h-4 w-4 text-muted-foreground" />
          Team Managers
          <span className="text-xs font-normal text-muted-foreground">(optional)</span>
        </div>

        {drivers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No drivers available to assign as managers.
          </p>
        ) : (
          <div className="space-y-2">
            <div className="grid gap-2">
              {drivers.map((driver) => (
                <div key={driver.onfleetWorkerId} className="flex items-center space-x-2">
                  <Checkbox
                    id={`manager-${driver.onfleetWorkerId}`}
                    checked={selectedManagers.includes(driver.onfleetWorkerId)}
                    onCheckedChange={() => handleManagerToggle(driver.onfleetWorkerId)}
                    disabled={processing}
                  />
                  <Label
                    htmlFor={`manager-${driver.onfleetWorkerId}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {driver.name}
                    {driver.displayName && (
                      <span className="ml-1 text-muted-foreground">
                        ({driver.displayName})
                      </span>
                    )}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Select drivers who can manage this team and assign tasks
            </p>
          </div>
        )}
      </div>

      <Separator />

      {/* Settings Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Settings className="h-4 w-4 text-muted-foreground" />
          Settings
        </div>

        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enableSelfAssignment"
              checked={enableSelfAssignment}
              onCheckedChange={(checked) => setEnableSelfAssignment(checked === true)}
              disabled={processing}
            />
            <Label
              htmlFor="enableSelfAssignment"
              className="text-sm font-normal cursor-pointer"
            >
              Enable self-assignment
            </Label>
          </div>
          <p className="text-xs text-muted-foreground pl-6">
            Allow drivers to self-assign tasks from the team's unassigned task pool
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex pt-2">
        <Button type="submit" disabled={processing} className="w-full">
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === 'create' ? 'Creating Team...' : 'Updating Team...'}
            </>
          ) : (
            mode === 'create' ? 'Create Team' : 'Update Team'
          )}
        </Button>
      </div>
    </form>
  );
});

TeamForm.displayName = 'TeamForm';
